import { gql, useQuery } from '@apollo/client';
import { Box, Stack, useBreakpointValue } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { NavigationButton } from '@/components/NavigationButton';
import {
  ECommunityAccessConfig,
  EEventsFeedAccessConfig,
} from '@/graphql-types/globalTypes';
import { useElementHeight } from '@/hooks/useElementHeight';
import { usePermissions } from '@/hooks/usePermissions';
import { useWindowHeight } from '@/hooks/useWindowHeight';
import { CalendarIcon, CornerUpLeftIcon } from '@/imports/ui/chakra/feather';
import {
  Blueprint,
  BlueprintPortal,
  useBlueprint,
} from '@/layouts/components/Blueprint';

import { CoffeeNav_Tenant } from './graphql-types/CoffeeNav_Tenant';

import { MyEvents } from './components/MyEvents';

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

const CoffeeContext = React.createContext<{ footerHeight: number }>({
  footerHeight: 0,
});

const TENANT = gql`
  query CoffeeNav_Tenant {
    tenant {
      result {
        _id
        eventsFeedConfig
      }
    }
  }
`;

export const CoffeeNav: React.FC = () => {
  const router = useRouter();
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();
  const permissions = usePermissions();
  const query = useQuery<CoffeeNav_Tenant>(TENANT);

  return (
    <Box
      alignSelf="flex-start"
      as="nav"
      display={{ base: 'none', lg: 'block' }}
      h={`${windowHeight - headerHeight}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 60, xl: 72 }}
      sx={scrollbar}
    >
      {!permissions.isAuthenticated &&
      query.data?.tenant.result?.eventsFeedConfig !==
        EEventsFeedAccessConfig.PUBLIC ? null : (
        <Stack spacing="10">
          <Stack>
            <NavigationButton
              href="/events?filter=upcoming"
              leftIcon={<CalendarIcon />}
              isActive={router.asPath.includes('filter=upcoming')}
            >
              Upcoming Events
            </NavigationButton>
            <NavigationButton
              href="/events?filter=past"
              leftIcon={<CornerUpLeftIcon />}
              isActive={router.asPath.includes('filter=past')}
            >
              Past Events
            </NavigationButton>
          </Stack>
          <MyEvents filter="upcoming" />
          <MyEvents filter="past" />
        </Stack>
      )}
    </Box>
  );
};

export const CoffeeMain: React.FC<{ isFullWidth?: boolean }> = (props) => {
  return (
    <Box as="main" py="4" px="1" mx="-1" flex="1" overflowY="auto">
      {props.children}
    </Box>
  );
};

export const CoffeeAside: React.FC = (props) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();

  const height = useBreakpointValue({
    lg: windowHeight - headerHeight,
  });

  return (
    <Box
      alignSelf="flex-start"
      as="aside"
      h={`${height}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ lg: 60, xl: 72 }}
      sx={scrollbar}
    >
      {props.children}
    </Box>
  );
};

export const CoffeePortal = BlueprintPortal;

export const Coffee: React.FC = (props) => {
  const footer = useElementHeight();

  return (
    <CoffeeContext.Provider value={{ footerHeight: footer.height }}>
      <Blueprint>
        <Box
          display="grid"
          gridTemplateColumns={{
            base: '1fr',
            lg: 'auto 1fr auto',
          }}
          gap={{ base: 2, md: 4 }}
          maxW="container.xl"
          mx="auto"
          px="4"
          mb={`${footer.height}px`}
        >
          {props.children}
        </Box>
      </Blueprint>
    </CoffeeContext.Provider>
  );
};
